import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Platform } from 'react-native';
import Constants from 'expo-constants';
import { useAuth } from './AuthContext';

const TickerContext = createContext();

// Sport categories for grouping
export const SPORT_CATEGORIES = [
  { id: 'all_basketball', label: '🏀 All Basketball', sport: 'basketball', isCategory: true },
  { id: 'all_football', label: '🏈 All Football', sport: 'football', isCategory: true },
  { id: 'all_baseball', label: '⚾ All Baseball', sport: 'baseball', isCategory: true },
  { id: 'all_hockey', label: '🏒 All Hockey', sport: 'hockey', isCategory: true },
  { id: 'all_soccer', label: '⚽ All Soccer', sport: 'soccer', isCategory: true },
  { id: 'all_tennis', label: '🎾 All Tennis', sport: 'tennis', isCategory: true },
  { id: 'all_combat', label: '🥊 All Combat Sports', sport: 'combat', isCategory: true },
];

// Individual league options - comprehensive list
export const AVAILABLE_TICKER_OPTIONS = [
  // Basketball
  { id: 'nba', label: 'NBA', sport: 'basketball' },
  { id: 'wnba', label: 'WNBA', sport: 'basketball' },
  { id: 'ncaab', label: 'NCAA Basketball', sport: 'basketball' },
  { id: 'nba_g_league', label: 'NBA G League', sport: 'basketball' },
  { id: 'euroleague', label: 'EuroLeague', sport: 'basketball' },
  
  // Football  
  { id: 'nfl', label: 'NFL', sport: 'football' },
  { id: 'ncaaf', label: 'NCAA Football', sport: 'football' },
  { id: 'cfl', label: 'CFL', sport: 'football' },
  { id: 'xfl', label: 'XFL/UFL', sport: 'football' },
  
  // Baseball
  { id: 'mlb', label: 'MLB', sport: 'baseball' },
  { id: 'ncaa_baseball', label: 'NCAA Baseball', sport: 'baseball' },
  { id: 'kbo', label: 'KBO (Korea)', sport: 'baseball' },
  { id: 'npb', label: 'NPB (Japan)', sport: 'baseball' },
  
  // Hockey
  { id: 'nhl', label: 'NHL', sport: 'hockey' },
  { id: 'ahl', label: 'AHL', sport: 'hockey' },
  { id: 'khl', label: 'KHL', sport: 'hockey' },
  
  // Soccer
  { id: 'mls', label: 'MLS', sport: 'soccer' },
  { id: 'epl', label: 'Premier League', sport: 'soccer' },
  { id: 'la_liga', label: 'La Liga', sport: 'soccer' },
  { id: 'bundesliga', label: 'Bundesliga', sport: 'soccer' },
  { id: 'serie_a', label: 'Serie A', sport: 'soccer' },
  { id: 'ligue_1', label: 'Ligue 1', sport: 'soccer' },
  { id: 'champions_league', label: 'Champions League', sport: 'soccer' },
  { id: 'europa_league', label: 'Europa League', sport: 'soccer' },
  { id: 'liga_mx', label: 'Liga MX', sport: 'soccer' },
  { id: 'nwsl', label: 'NWSL', sport: 'soccer' },
  
  // Tennis
  { id: 'atp', label: 'ATP Tour', sport: 'tennis' },
  { id: 'wta', label: 'WTA Tour', sport: 'tennis' },
  { id: 'itf', label: 'ITF', sport: 'tennis' },
  
  // Combat Sports
  { id: 'ufc', label: 'UFC', sport: 'combat' },
  { id: 'bellator', label: 'Bellator', sport: 'combat' },
  { id: 'pfl', label: 'PFL', sport: 'combat' },
  { id: 'boxing', label: 'Boxing', sport: 'combat' },
  
  // Other
  { id: 'pga', label: 'PGA Golf', sport: 'golf' },
  { id: 'lpga', label: 'LPGA Golf', sport: 'golf' },
  { id: 'nascar', label: 'NASCAR', sport: 'racing' },
  { id: 'f1', label: 'Formula 1', sport: 'racing' },
  { id: 'esports', label: 'Esports', sport: 'esports' },
];

// Default ticker preferences - most popular leagues
export const DEFAULT_TICKER_LEAGUES = [
  'nba', 'nfl', 'mlb', 'nhl', 'ncaab', 'ncaaf', 
  'mls', 'epl', 'champions_league',
  'atp', 'wta', 'ufc'
];

// Name format options for ticker
export const TICKER_NAME_FORMATS = [
  { id: 'full', label: 'Full Names', example: 'Cleveland Cavaliers, Carlos Alcaraz' },
  { id: 'short', label: 'Short Names', example: 'Cavaliers, Alcaraz' },
  { id: 'abbreviated', label: 'Abbreviated', example: 'CLE, C. Alcaraz' },
];

// Helper to get all leagues for a sport category
export const getLeaguesBySport = (sport) => {
  if (sport === 'combat') {
    return AVAILABLE_TICKER_OPTIONS.filter(opt => opt.sport === 'combat').map(opt => opt.id);
  }
  return AVAILABLE_TICKER_OPTIONS.filter(opt => opt.sport === sport).map(opt => opt.id);
};

export const useTicker = () => {
  const context = useContext(TickerContext);
  if (!context) {
    throw new Error('useTicker must be used within a TickerProvider');
  }
  return context;
};

// Helper to get functions base URL
const getFunctionsUrl = () => {
  const isLocalhost = Platform.OS === 'web' && 
    (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'));
  
  if (isLocalhost) {
    return 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1';
  }
  
  return (
    (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
    || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
    || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
    || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
    || 'https://us-central1-bet-optimized-fe922.cloudfunctions.net'
  );
};

export const TickerProvider = ({ children }) => {
  const { userProfile } = useAuth();
  
  // Ticker default visibility (persisted to localStorage - controlled by settings page)
  const [isTickerEnabled, setIsTickerEnabled] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = window.localStorage.getItem('tickerEnabled');
      return saved === null ? true : saved === 'true'; // Default to enabled
    }
    return true;
  });
  
  // Ticker collapsed state (session only - controlled by chevron on ticker)
  const [isTickerCollapsed, setIsTickerCollapsed] = useState(false);
  
  // User's league/sport preferences for ticker (from profile or localStorage)
  const [tickerLeagues, setTickerLeagues] = useState(() => {
    // First check localStorage for quick initial render
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = window.localStorage.getItem('tickerLeagues');
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch {}
      }
    }
    return DEFAULT_TICKER_LEAGUES;
  });
  
  // Name format preference: 'full', 'short', or 'abbreviated'
  const [tickerNameFormat, setTickerNameFormat] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = window.localStorage.getItem('tickerNameFormat');
      if (saved) return saved;
    }
    return 'short'; // Default to short names (e.g., "Cavaliers" instead of "Cleveland Cavaliers")
  });
  
  // ⭐ SGP Indicator setting - show when games have cached SGPs
  const [showTickerSGPIndicator, setShowTickerSGPIndicator] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = window.localStorage.getItem('tickerSGPIndicator');
      return saved === 'true';
    }
    return false; // Default to off
  });
  
  // ⭐ SGP-only mode - only show games with cached SGPs in ticker
  const [tickerSGPOnly, setTickerSGPOnly] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = window.localStorage.getItem('tickerSGPOnly');
      return saved === 'true';
    }
    return false; // Default to off
  });
  
  // SGP cache status for fixtures { fixture_id: boolean }
  const [sgpCacheStatus, setSgpCacheStatus] = useState({});
  const sgpFetchingRef = useRef(false);
  
  // Sync ticker leagues from user profile when it loads
  const profileSyncedRef = useRef(false);
  const needsRefetchRef = useRef(false); // Flag to trigger refetch after profile sync
  useEffect(() => {
    if (userProfile?.preferences?.tickerLeagues && !profileSyncedRef.current) {
      console.log('[Ticker] Syncing preferences from profile:', userProfile.preferences.tickerLeagues.join(', '));
      setTickerLeagues(userProfile.preferences.tickerLeagues);
      profileSyncedRef.current = true;
      needsRefetchRef.current = true; // Mark that we need to refetch
      // Also save to localStorage for faster initial load next time
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem('tickerLeagues', JSON.stringify(userProfile.preferences.tickerLeagues));
      }
    }
    if (userProfile?.preferences?.tickerNameFormat) {
      setTickerNameFormat(userProfile.preferences.tickerNameFormat);
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem('tickerNameFormat', userProfile.preferences.tickerNameFormat);
      }
    }
  }, [userProfile?.preferences?.tickerLeagues, userProfile?.preferences?.tickerNameFormat]);
  
  // Fixtures data - split into initial (fast) and full batches
  const [fixtures, setFixtures] = useState([]);
  const [rawFixtures, setRawFixtures] = useState([]); // Store unfiltered fixtures for re-filtering
  const [isLoading, setIsLoading] = useState(true); // Start as loading
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const lastFetchedRef = useRef(null);
  const fetchingRef = useRef(false);
  const fullFetchDoneRef = useRef(false);
  
  // Save ticker preference to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('tickerEnabled', String(isTickerEnabled));
    }
  }, [isTickerEnabled]);
  
  // Update ticker leagues preference
  const updateTickerLeagues = useCallback((leagues) => {
    setTickerLeagues(leagues);
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('tickerLeagues', JSON.stringify(leagues));
    }
  }, []);
  
  // Update ticker name format preference
  const updateTickerNameFormat = useCallback((format) => {
    setTickerNameFormat(format);
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('tickerNameFormat', format);
    }
  }, []);
  
  // ⭐ Update SGP indicator preference
  const updateShowTickerSGPIndicator = useCallback((enabled) => {
    setShowTickerSGPIndicator(enabled);
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('tickerSGPIndicator', String(enabled));
    }
  }, []);
  
  // ⭐ Update SGP-only mode
  const updateTickerSGPOnly = useCallback((enabled) => {
    setTickerSGPOnly(enabled);
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('tickerSGPOnly', String(enabled));
    }
    // If enabling SGP-only, also enable the SGP indicator
    if (enabled && !showTickerSGPIndicator) {
      setShowTickerSGPIndicator(true);
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem('tickerSGPIndicator', 'true');
      }
    }
  }, [showTickerSGPIndicator]);
  
  // ⭐ Background fetch SGP cache status for ticker fixtures
  const fetchSGPCacheStatus = useCallback(async (fixturesList) => {
    if (!fixturesList || fixturesList.length === 0 || sgpFetchingRef.current) return;
    
    sgpFetchingRef.current = true;
    const functionsUrl = getFunctionsUrl();
    
    try {
      // Fetch in batches of 5 to avoid overwhelming the server
      const batchSize = 5;
      const newStatus = { ...sgpCacheStatus };
      
      for (let i = 0; i < fixturesList.length; i += batchSize) {
        const batch = fixturesList.slice(i, i + batchSize);
        
        await Promise.all(
          batch.map(async (fixture) => {
            const fixtureId = fixture.fixture_id || fixture.id;
            
            // Skip if already fetched
            if (newStatus[fixtureId] !== undefined) return;
            
            try {
              const response = await fetch(`${functionsUrl}/getCachedSGPs?fixture_id=${fixtureId}&stats_only=true`);
              const data = await response.json();
              
              // Mark as having SGPs if response is successful and has SGP data
              newStatus[fixtureId] = data.success && data.stats?.total_sgps > 0;
            } catch {
              // Silently fail - just mark as no SGPs
              newStatus[fixtureId] = false;
            }
          })
        );
        
        // Update state after each batch for progressive updates
        setSgpCacheStatus({ ...newStatus });
      }
      
      console.log('[Ticker] SGP cache status fetched for', fixturesList.length, 'fixtures');
    } catch (err) {
      console.warn('[Ticker] Error fetching SGP cache status:', err);
    } finally {
      sgpFetchingRef.current = false;
    }
  }, [sgpCacheStatus]);
  
  // Toggle ticker visibility
  const toggleTicker = useCallback(() => {
    setIsTickerEnabled(prev => !prev);
  }, []);
  
  const enableTicker = useCallback(() => {
    setIsTickerEnabled(true);
  }, []);
  
  const disableTicker = useCallback(() => {
    setIsTickerEnabled(false);
  }, []);
  
  // Helper to filter fixtures by time relevance
  const filterByTimeRelevance = (fixturesData) => {
    const now = Date.now();
    const twelveHoursAgo = now - (12 * 60 * 60 * 1000);
    const oneWeekFromNow = now + (7 * 24 * 60 * 60 * 1000);
    
    return fixturesData.filter(fixture => {
      if (!fixture.start_date) return true;
      
      let gameTime;
      if (fixture.start_date._seconds) {
        gameTime = fixture.start_date._seconds * 1000;
      } else if (typeof fixture.start_date === 'string') {
        gameTime = new Date(fixture.start_date).getTime();
      } else if (fixture.start_date.seconds) {
        gameTime = fixture.start_date.seconds * 1000;
      } else {
        gameTime = new Date(fixture.start_date).getTime();
      }
      
      return gameTime >= twelveHoursAgo && gameTime <= oneWeekFromNow;
    });
  };
  
  // Helper to filter fixtures by user's league preferences
  const filterByUserPreferences = useCallback((fixturesData) => {
    if (!tickerLeagues || tickerLeagues.length === 0) return fixturesData;
    
    return fixturesData.filter(fixture => {
      const fixtureLeague = (fixture.league || '').toLowerCase();
      const fixtureSport = (fixture.sport || '').toLowerCase();
      
      return tickerLeagues.some(pref => {
        const prefLower = pref.toLowerCase();
        // Check if league matches
        if (fixtureLeague.includes(prefLower) || prefLower.includes(fixtureLeague)) return true;
        // Check if sport matches
        if (fixtureSport.includes(prefLower) || prefLower.includes(fixtureSport)) return true;
        // Special cases
        if (prefLower === 'soccer' && (fixtureLeague.includes('premier') || fixtureLeague.includes('la liga') || fixtureLeague.includes('bundesliga') || fixtureLeague.includes('serie a') || fixtureLeague.includes('mls') || fixtureLeague.includes('champions'))) return true;
        if (prefLower === 'tennis' && (fixtureLeague.includes('atp') || fixtureLeague.includes('wta'))) return true;
        if (prefLower === 'mma' && (fixtureLeague.includes('ufc') || fixtureLeague.includes('mma'))) return true;
        return false;
      });
    });
  }, [tickerLeagues]);
  
  // Helper to get each team's next game only (not all games)
  // This shows one game per team, sorted by start time
  const getNextGamePerTeam = useCallback((fixturesData) => {
    const now = Date.now();
    const teamsNextGame = new Map(); // Map<teamName, fixture>
    
    // Sort fixtures by start time (earliest first)
    const sortedFixtures = [...fixturesData].sort((a, b) => {
      const getTime = (f) => {
        if (!f.start_date) return Infinity;
        if (f.start_date._seconds) return f.start_date._seconds * 1000;
        if (f.start_date.seconds) return f.start_date.seconds * 1000;
        return new Date(f.start_date).getTime();
      };
      return getTime(a) - getTime(b);
    });
    
    // For each fixture, add it as the team's next game if they don't have one yet
    sortedFixtures.forEach(fixture => {
      const getTime = (f) => {
        if (!f.start_date) return Infinity;
        if (f.start_date._seconds) return f.start_date._seconds * 1000;
        if (f.start_date.seconds) return f.start_date.seconds * 1000;
        return new Date(f.start_date).getTime();
      };
      
      const gameTime = getTime(fixture);
      
      // Only consider games that haven't started yet OR are currently live
      // (Skip games that ended more than 3 hours ago)
      const threeHoursAgo = now - (3 * 60 * 60 * 1000);
      if (gameTime < threeHoursAgo && !fixture.is_live) return;
      
      // Check home team
      const homeTeam = fixture.home_team;
      if (homeTeam && !teamsNextGame.has(homeTeam)) {
        teamsNextGame.set(homeTeam, fixture);
      }
      
      // Check away team
      const awayTeam = fixture.away_team;
      if (awayTeam && !teamsNextGame.has(awayTeam)) {
        teamsNextGame.set(awayTeam, fixture);
      }
    });
    
    // Get unique fixtures (a fixture might be the next game for both teams)
    const uniqueFixtures = [...new Set(teamsNextGame.values())];
    
    // Sort again by start time
    return uniqueFixtures.sort((a, b) => {
      const getTime = (f) => {
        if (!f.start_date) return Infinity;
        if (f.start_date._seconds) return f.start_date._seconds * 1000;
        if (f.start_date.seconds) return f.start_date.seconds * 1000;
        return new Date(f.start_date).getTime();
      };
      return getTime(a) - getTime(b);
    });
  }, []);
  
  // Fast fetch using optimized ticker endpoint
  const fetchTickerFixtures = useCallback(async (force = false) => {
    // Prevent concurrent fetches
    if (fetchingRef.current) return;
    
    // Don't refetch if we fetched recently (within 2 minutes) unless forced
    if (!force && lastFetchedRef.current && (Date.now() - lastFetchedRef.current) < 120000) {
      return;
    }
    
    fetchingRef.current = true;
    setError(null);
    setIsLoading(true);
    
    const functionsUrl = getFunctionsUrl();
    
    try {
      // Build league params from user preferences
      const leagueParams = tickerLeagues?.length > 0 
        ? `&leagues=${tickerLeagues.join(',')}`
        : '';
      
      const quickUrl = `${functionsUrl}/getTickerFixtures?limit=30${leagueParams}`;
      console.log('[Ticker] Fetching:', quickUrl);
      
      // PHASE 1: Quick initial load (small batch for fast display)
      const quickResponse = await fetch(quickUrl);
      
      if (quickResponse.ok) {
        const quickData = await quickResponse.json();
        if (quickData.success && quickData.fixtures?.length > 0) {
          setRawFixtures(quickData.fixtures);
          // Filter to show only each team's next game
          const nextGamesOnly = getNextGamePerTeam(quickData.fixtures);
          setFixtures(nextGamesOnly);
          setIsLoading(false); // Show ticker immediately!
          
          const leagues = [...new Set(nextGamesOnly.map(f => f.league).filter(Boolean))];
          console.log('[Ticker] Quick load:', nextGamesOnly.length, 'fixtures (next game per team),', leagues.length, 'leagues:', leagues.join(', '));
        }
      }
      
      // PHASE 2: Load more in background for variety (don't block UI)
      setIsLoadingMore(true);
      const fullResponse = await fetch(`${functionsUrl}/getTickerFixtures?limit=100${leagueParams}`);
      
      if (fullResponse.ok) {
        const fullData = await fullResponse.json();
        if (fullData.success && fullData.fixtures?.length > 0) {
          setRawFixtures(fullData.fixtures);
          // Filter to show only each team's next game
          const nextGamesOnly = getNextGamePerTeam(fullData.fixtures);
          setFixtures(nextGamesOnly);
          
          const leagues = [...new Set(nextGamesOnly.map(f => f.league).filter(Boolean))];
          console.log('[Ticker] Full load:', nextGamesOnly.length, 'fixtures (next game per team),', leagues.length, 'leagues:', leagues.join(', '));
          
          fullFetchDoneRef.current = true;
        }
      }
      
      lastFetchedRef.current = Date.now();
      
    } catch (err) {
      console.error('[Ticker] Fetch error:', err);
      
      // Fallback to getAllCachedFixtures
      try {
        const fallbackResponse = await fetch(`${functionsUrl}/getAllCachedFixtures?limit=50&include_odds=false`);
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          if (fallbackData.success && fallbackData.fixtures?.length > 0) {
            let fixtures = filterByTimeRelevance(fallbackData.fixtures);
            setRawFixtures(fixtures);
            const filtered = filterByUserPreferences(fixtures);
            // Filter to show only each team's next game
            const nextGamesOnly = getNextGamePerTeam(filtered);
            setFixtures(nextGamesOnly);
            console.log('[Ticker] Fallback loaded', nextGamesOnly.length, 'fixtures (next game per team)');
          }
        }
      } catch (fallbackErr) {
        console.error('[Ticker] Fallback also failed:', fallbackErr);
      }
      
      setError(err.message);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
      fetchingRef.current = false;
    }
  }, [tickerLeagues, filterByUserPreferences, getNextGamePerTeam]);
  
  // Track if initial preferences have loaded
  const prefsLoadedRef = useRef(false);
  
  // Initial fetch - wait briefly for user profile to load preferences
  useEffect(() => {
    // Small delay to allow user profile to load first
    const timer = setTimeout(() => {
      // At this point, tickerLeagues should be populated from localStorage or user profile
      console.log('[Ticker] Initial fetch with leagues:', tickerLeagues?.join(', ') || 'none (showing all)');
      fetchTickerFixtures(true);
      prefsLoadedRef.current = true;
    }, 500); // Give more time for profile to load
    
    return () => clearTimeout(timer);
  }, []); // Only run on mount
  
  // Fetch when ticker is enabled and no fixtures
  useEffect(() => {
    if (isTickerEnabled && fixtures.length === 0 && !fetchingRef.current && prefsLoadedRef.current) {
      fetchTickerFixtures(true);
    }
  }, [isTickerEnabled, fixtures.length, fetchTickerFixtures]);
  
  // Periodic refresh every 5 minutes when ticker is enabled
  useEffect(() => {
    if (!isTickerEnabled) return;
    
    const interval = setInterval(() => {
      fetchTickerFixtures(true); // Force refresh
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [isTickerEnabled, fetchTickerFixtures]);
  
  // Refetch fixtures when ticker preferences change - ALWAYS refetch, don't skip
  useEffect(() => {
    // Check if we need to refetch due to profile sync OR preference changes
    if (needsRefetchRef.current || (prefsLoadedRef.current && tickerLeagues && tickerLeagues.length > 0)) {
      console.log('[Ticker] Preferences changed, refetching with:', tickerLeagues?.join(', ') || 'none');
      needsRefetchRef.current = false; // Clear the flag
      fetchTickerFixtures(true); // Force refetch with new preferences
    }
  }, [tickerLeagues, fetchTickerFixtures]);
  
  // ⭐ Fetch SGP cache status when setting is enabled and fixtures are loaded
  useEffect(() => {
    if (showTickerSGPIndicator && fixtures.length > 0 && !isLoading) {
      // Small delay to not interfere with initial render
      const timer = setTimeout(() => {
        fetchSGPCacheStatus(fixtures);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [showTickerSGPIndicator, fixtures, isLoading, fetchSGPCacheStatus]);
  
  // Header height constants
  const NAVBAR_HEIGHT = 56;
  const TICKER_HEIGHT = 40;
  const TICKER_NAVBAR_GAP = 8; // Space between ticker and navbar
  const TOP_SPACER_HEIGHT = 16; // Space from top when ticker is hidden/collapsed
  
  // Calculate total header height based on current visibility state
  const tickerActuallyVisible = isTickerEnabled && !isTickerCollapsed;
  const totalHeaderHeight = tickerActuallyVisible 
    ? NAVBAR_HEIGHT + TICKER_HEIGHT + TICKER_NAVBAR_GAP
    : NAVBAR_HEIGHT + TOP_SPACER_HEIGHT + TICKER_NAVBAR_GAP;
  
  // Update CSS variable for header height (web only)
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      document.documentElement.style.setProperty('--header-height', `${totalHeaderHeight}px`);
      document.documentElement.style.setProperty('--navbar-height', `${NAVBAR_HEIGHT}px`);
      document.documentElement.style.setProperty('--ticker-height', tickerActuallyVisible ? `${TICKER_HEIGHT}px` : `${TOP_SPACER_HEIGHT}px`);
    }
  }, [tickerActuallyVisible, totalHeaderHeight]);
  
  // Toggle collapsed state (chevron on ticker)
  const toggleCollapsed = useCallback(() => {
    setIsTickerCollapsed(prev => !prev);
  }, []);
  
  // Computed: is ticker actually visible right now?
  const isTickerVisible = isTickerEnabled && !isTickerCollapsed;
  
  const value = {
    // State
    isTickerEnabled,      // Default setting (from settings page)
    isTickerCollapsed,    // Session collapse state (from chevron)
    isTickerVisible,      // Computed: actually showing right now
    fixtures,
    isLoading,
    isLoadingMore,        // True when loading additional fixtures in background
    error,
    lastFetched: lastFetchedRef.current,
    
    // User preferences
    tickerLeagues,        // User's league/sport preferences
    tickerNameFormat,     // Name format: 'full', 'short', or 'abbreviated'
    showTickerSGPIndicator, // ⭐ Whether to show SGP cache indicator
    tickerSGPOnly,        // ⭐ Whether to only show games with cached SGPs
    sgpCacheStatus,       // ⭐ Map of fixture_id -> has cached SGPs
    
    // Layout helpers
    navbarHeight: NAVBAR_HEIGHT,
    tickerHeight: TICKER_HEIGHT,
    totalHeaderHeight,
    
    // Actions
    toggleTicker,         // Toggle default setting (settings page)
    enableTicker,
    disableTicker,
    toggleCollapsed,      // Toggle collapse (chevron on ticker)
    expandTicker: () => setIsTickerCollapsed(false),
    collapseTicker: () => setIsTickerCollapsed(true),
    refreshFixtures: () => fetchTickerFixtures(true),
    updateTickerLeagues,  // Update user's league preferences
    updateTickerNameFormat, // Update name format preference
    updateShowTickerSGPIndicator, // ⭐ Toggle SGP indicator setting
    updateTickerSGPOnly,  // ⭐ Toggle SGP-only mode
  };
  
  return (
    <TickerContext.Provider value={value}>
      {children}
    </TickerContext.Provider>
  );
};

export default TickerContext;

